import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '@/src/lib/axios/axios';
import {
  DEFAULT_MESSAGES_LIMIT,
  MESSAGING_API_BASE,
  messagingQueryKeys,
} from '../config';
import { SendMessageBody, MessageResponse } from '../types/backend';

export type SendMessageDTO = {
  channelId: number;
  content?: string;
  replyToMessageId?: number;
  storedFileIds?: number[];
};

const sendMessage = async (data: SendMessageDTO): Promise<MessageResponse> => {
  const { channelId, content, replyToMessageId, storedFileIds } = data;

  const body: SendMessageBody = {
    content,
    'reply-to-message-id': replyToMessageId,
    'stored-file-ids': storedFileIds,
  };

  return axios.post(
    `${MESSAGING_API_BASE}/channels/${channelId}/messages`,
    body
  );
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendMessageDTO) => sendMessage(data),
    onSuccess: (apiMessage, variables) => {
      const { channelId } = variables;

      /**
       * IMPORTANT: Replace optimistic placeholder with real API response immediately.
       *
       * Previous flow (socket-dependent):
       * 1. Show optimistic placeholder
       * 2. Wait up to 2 seconds for socket event
       * 3. Replace placeholder when socket arrives
       *
       * New flow (API-first):
       * 1. Show optimistic placeholder
       * 2. API responds → IMMEDIATELY show real message with delivery-status
       * 3. Socket arrives later → deduplicated by canonicalization logic
       *
       * Benefits:
       * - No 2-second delay waiting for socket
       * - Real delivery-status shown instantly from API
       * - Socket still provides eventual consistency for other users' messages
       */
      const key = messagingQueryKeys.messagesList(channelId, {
        limit: DEFAULT_MESSAGES_LIMIT,
      });

      queryClient.setQueryData(key as any, (old: any) => {
        if (!old?.pages) return old;

        // Find and replace the optimistic placeholder with the real API message
        const newPages = old.pages.map((page: any[]) =>
          page.map((msg: any) => {
            /**
             * Match optimistic placeholder by:
             * 1. Has __optimistic flag
             * 2. Same content (message text)
             * 3. Same sender (current user)
             *
             * When found, replace entire placeholder with API response which includes:
             * - Real message ID from database
             * - Server timestamp (date-added)
             * - Delivery status (pending/sent/delivered/failed)
             * - All other backend fields
             */
            if (
              msg.__optimistic &&
              msg.content === apiMessage.content &&
              msg['sender-user-id'] === apiMessage['sender-user-id']
            ) {
              // Replace with API response - this is now the canonical version
              // Socket event will be deduplicated when it arrives later
              return apiMessage;
            }
            return msg;
          })
        );

        return { ...old, pages: newPages };
      });

      // DO NOT update channel last-message-at here for outgoing messages.
      // Reason: For WhatsApp channels, updating last-message-at triggers the
      // fallback window calculation in useWhatsAppChannel, which incorrectly
      // activates the 24-hour service window when you send an outgoing message.
      // The service window should only activate on INCOMING WhatsApp messages from
      // the client (handled by backend webhook → socket → useMessagingSubscriptions).
      // The backend is the source of truth for whatsapp.window-expires-at.
      //
      // The socket handler (MESSAGING_NEW_MESSAGE) already updates channel
      // metadata for incoming messages, which moves the channel to the top of
      // the conversation list. For outgoing messages, the channel will move to
      // the top when the socket event arrives (deduplicated with the API response).

      // Invalidate unread counts (this channel now has a new message)
      queryClient.invalidateQueries(messagingQueryKeys.unreadCounts());
    },
  });
};
